import React, { useState } from "react";
import {
  Box,
  Card,
  CardBody,
  Text,
  HStack,
  VStack,
  SimpleGrid,
  Spinner,
  Switch,
  IconButton,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
} from "@chakra-ui/react";
import { DeleteIcon, CopyIcon } from "@chakra-ui/icons";
import { FaPlay } from "react-icons/fa";
import { Header } from "build-ts/metadata";
import { setScheduledJobIsActive, deleteScheduledJob, runJobAsync, getScheduledJob, putScheduledJob } from "services/ScheduleServices";
import showToast from "hooks/useCustomToast";
import { MetadataTypeEnum, metadataTypeEnumToJSON } from "build-ts/enums";
import { ScheduledJobProto } from "build-ts/schedule";
import { v4 as uuidv4 } from "uuid";

interface JobListProps {
  jobs: Header[];
  isLoadingJobs: boolean;
  onJobClick?: (job: Header) => void;
  onRefresh?: () => void;
}

const JobList: React.FC<JobListProps> = ({
  jobs,
  isLoadingJobs,
  onJobClick,
  onRefresh,
}) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<Header | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [runningJobId, setRunningJobId] = useState<string | null>(null);
  const [copyingJobId, setCopyingJobId] = useState<string | null>(null);

  const handleToggleActive = async (job: Header, isActive: boolean) => {
    if (!job.id) return;

    const response = await setScheduledJobIsActive(job.id, isActive);
    if (response) {
      showToast(
        isActive ? "Job activated" : "Job deactivated",
        `The job "${job.name}" has been ${isActive ? "activated" : "deactivated"}.`,
        "success"
      );
      onRefresh?.();
    }
  };

  const handleRunJob = async (job: Header) => {
    if (!job.id) return;

    setRunningJobId(job.id);
    await runJobAsync(job.id, metadataTypeEnumToJSON(MetadataTypeEnum.ANALYST));
    setRunningJobId(null);

    showToast(
      "Job is running",
      "The scheduled job has been triggered and is now running.",
      "success"
    );
    onRefresh?.();
  };

  const handleCopyJob = async (job: Header) => {
    if (!job.id) return;

    setCopyingJobId(job.id);

    try {
      // Fetch the full job details
      const jobDetails = await getScheduledJob(job.id, false);

      if (!jobDetails) {
        showToast("Failed to fetch job details", "", "error");
        setCopyingJobId(null);
        return;
      }

      // Create a new job with copied details
      const newJob = ScheduledJobProto.create({
        header: Header.create({
          id: uuidv4(), // Generate new ID
          name: `${jobDetails.header?.name || "Untitled"} (Copy)`,
          type: MetadataTypeEnum.SCHEDULED_JOB,
          owner_id: jobDetails.header?.owner_id,
        }),
        cron_expr: jobDetails.cron_expr,
        type: jobDetails.type,
        is_active: false,
        scheduled_analyst: jobDetails.scheduled_analyst,
      });

      // Save the new job
      const response = await putScheduledJob(newJob);

      if (response) {
        showToast(
          "Job copied successfully",
          `"${newJob.header?.name}" has been created.`,
          "success"
        );
        onRefresh?.();
      } else {
        showToast("Failed to copy job", "", "error");
      }
    } catch (error) {
      console.error("Error copying job:", error);
      showToast("Failed to copy job", "", "error");
    } finally {
      setCopyingJobId(null);
    }
  };

  const handleDeleteClick = (job: Header) => {
    setJobToDelete(job);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!jobToDelete?.id) return;

    setIsDeleting(true);
    const response = await deleteScheduledJob(jobToDelete.id);
    setIsDeleting(false);

    if (response) {
      showToast(
        "Job deleted",
        `The job "${jobToDelete.name}" has been deleted.`,
        "success"
      );
      setDeleteModalOpen(false);
      setJobToDelete(null);
      onRefresh?.();
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setJobToDelete(null);
  };

  return (
    <Box height="calc(100% - 60px)" overflowY="auto">
      {isLoadingJobs ? (
        <Box
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Spinner size="sm" color="purple.500" thickness="4px" />
        </Box>
      ) : jobs.length === 0 ? (
        <Box
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text color="gray.500" fontSize="md">
            No jobs match your search. Try different keywords.
          </Text>
        </Box>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {jobs.map((job) => (
            <Card
              key={job.id}
              cursor="pointer"
              _hover={{ shadow: "md", borderColor: "purple.300" }}
              transition="all 0.2s"
              border={job.has_active_schedule ? "2px solid" : "1px solid"}
              borderColor={job.has_active_schedule ? "green.500" : "gray.500"}
              bg={job.has_active_schedule ? "green.50" : "transparent"}
              color={job.has_active_schedule ? "green.500" : "fg.default"}
              borderRadius="md"
              onClick={() => onJobClick?.(job)}
            >
              <CardBody>
                <VStack align="stretch" spacing={3}>
                  {/* Job Name and Actions */}
                  <VStack align="stretch" spacing={3}>
                    <Text
                      fontSize="md"
                      fontWeight="500"
                      color="fg.default"
                      m={0}
                      flex={1}
                      textAlign="left"
                    >
                      {job.name}
                    </Text>
                    <HStack justifyContent="space-between" align="center">
                      {/* Author Name */}
                      {job.author_name && (
                        <Text
                          fontSize="sm"
                          fontWeight="400"
                          color="gray.500"
                          m={0}
                          flex={1}
                          textAlign="left"
                          fontStyle="italic"
                        >
                          Created by {job.author_name || ""}
                        </Text>
                      )}
                      {/* Actions Buttons */}
                      <HStack justifyContent="flex-end" spacing={2} onClick={(e) => e.stopPropagation()}>
                        {/* Toggle Active/Inactive */}
                        <Tooltip
                          label={job.has_active_schedule ? "Active" : "Inactive"}
                          placement="top"
                        >
                          <HStack spacing={2}>
                            <Text fontSize="xs" color="gray.600" m={0}>
                              {job.has_active_schedule ? "Active" : "Inactive"}
                            </Text>
                            <Switch
                              isChecked={job.has_active_schedule || false}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleToggleActive(job, e.target.checked);
                              }}
                              colorScheme="purple"
                              size="sm"
                              m={0}
                            />
                          </HStack>
                        </Tooltip>
                        {/* Run Button */}
                        <Tooltip label="Run job now" placement="top">
                          <IconButton
                            aria-label="Run job"
                            icon={<FaPlay />}
                            size="sm"
                            colorScheme="purple"
                            variant="ghost"
                            isLoading={runningJobId === job.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRunJob(job);
                            }}
                          />
                        </Tooltip>
                        {/* Copy Button */}
                        <Tooltip label="Copy job" placement="top">
                          <IconButton
                            aria-label="Copy job"
                            icon={<CopyIcon />}
                            size="sm"
                            colorScheme="blue"
                            variant="ghost"
                            isLoading={copyingJobId === job.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyJob(job);
                            }}
                          />
                        </Tooltip>
                        {/* Delete Button */}
                        <Tooltip label="Delete job" placement="top">
                          <IconButton
                            aria-label="Delete job"
                            icon={<DeleteIcon />}
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(job);
                            }}
                          />
                        </Tooltip>
                      </HStack>
                    </HStack>
                  </VStack>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      )}

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModalOpen} onClose={handleDeleteCancel} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Job</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Are you sure you want to delete the job "{jobToDelete?.name}"?
              This action cannot be undone.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleDeleteCancel} isDisabled={isDeleting}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleDeleteConfirm}
              isLoading={isDeleting}
              loadingText="Deleting..."
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default JobList;

